import { useQuery, type ApolloError } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { SelectOption, FileData } from '../../../../../components';
import { getOptionsLabel } from '../../../../Monitor/components';
import { formatDateString, processRawData } from '../../../../../utils';

const GET_STUDENT_PARTICULAR = gql`
  query ($applicationId: String!) {
    programmeChoiceMetadata {
      countryList {
        value
        label
      }

      disabilityTypeList {
        value
        label
      }

      provinceList {
        value
        label
      }

      rpgJoinPhdPartnerList {
        value
        label
      }

      prgCollabPhdPartnerList {
        value
        label
      }
    }

    studentParticular(applicationId: $applicationId) {
      applicationId
      applicationNumber
      studentNumber
      residenceType
      offeredToYear
      offeredToSemester
      addressCountryRegion
      jointPhDProgPartnerInstitution
      collabPhDProgPartnerInstitution
      nameInEnglishSurname
      nameInEnglishGivenName
      nameInChinese
      identityType
      identityNumber
      dateOfBirth
      gender
      nationalityCountryRegion
      nationalityProvince
      addressLine1
      addressLine2
      addressLine3
      contactPhoneAreaCode
      contactPhoneNumber
      mobileAreaCode
      mobileNumber
      emailAddress
      localIndicator
      disabilityType
      disabilityDetail
      photoFile {
        id
        link
        name
      }
      programmeCode
      programmeSubcode
      HKPFSInd
    }
  }
`;

type GetStudentParticular = {
  studentParticular: StudentParticular;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

export type StudentParticular = {
  applicationId: string;
  applicationNumber: string;
  studentNumber: string;
  nameInEnglishSurname: string;
  nameInEnglishGivenName: string;
  nameInChinese: string;
  identityType: string;
  identityNumber: string;
  dateOfBirth: string;
  gender: string;
  nationalityCountryRegion: string;
  nationalityProvince: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressCountryRegion?: string;
  contactPhoneAreaCode: string;
  contactPhoneNumber: string;
  mobileAreaCode: string;
  mobileNumber: string;
  emailAddress: string;
  residenceType?: string;
  localIndicator: string;
  disabilityType: string;
  disabilityDetail: string;
  photoFile?: FileData | null;
  offeredToYear?: string;
  offeredToSemester?: string;
  programmeCode: string;
  programmeSubcode: string;
  HKPFSInd?: string;
  jointPhDProgPartnerInstitution?: string;
  collabPhDProgPartnerInstitution?: string;
};

export const useGetStudentParticular = (
  applicationId: string,
): GetStudentParticular => {
  const { data, loading, error, refetch } = useQuery(GET_STUDENT_PARTICULAR, {
    variables: { applicationId },
    skip: !applicationId,
    fetchPolicy: 'network-only',
  });

  const countryList: SelectOption[] =
    data?.programmeChoiceMetadata?.countryList ?? [];

  const disabilityTypeList: SelectOption[] =
    data?.programmeChoiceMetadata?.disabilityTypeList ?? [];

  const provinceOptions: SelectOption[] =
    data?.programmeChoiceMetadata?.provinceList ?? [];

  const rpgJoinPhdPartnerList: SelectOption[] =
    data?.programmeChoiceMetadata?.rpgJoinPhdPartnerList ?? [];

  const prgCollabPhdPartnerList: SelectOption[] =
    data?.programmeChoiceMetadata?.prgCollabPhdPartnerList ?? [];

  const {
    applicationId: id,
    applicationNumber = '',
    studentNumber = '',
    residenceType = 'N/A',
    offeredToYear = 'N/A',
    offeredToSemester = 'N/A',
    addressCountryRegion = 'N/A',
    jointPhDProgPartnerInstitution = 'N/A',
    collabPhDProgPartnerInstitution = 'N/A',
    nameInEnglishSurname = '',
    nameInEnglishGivenName = '',
    nameInChinese = '',
    identityType = '',
    identityNumber = '',
    dateOfBirth,
    gender = '',
    nationalityCountryRegion = '',
    nationalityProvince = '',
    addressLine1 = '',
    addressLine2 = '',
    addressLine3 = '',
    contactPhoneAreaCode = '',
    contactPhoneNumber = '',
    mobileAreaCode = '',
    mobileNumber = '',
    emailAddress = '',
    localIndicator = '',
    disabilityType = '',
    disabilityDetail = '',
    photoFile,
    programmeCode = '',
    programmeSubcode = '',
    HKPFSInd = '',
  } = data?.studentParticular ?? {};

  const studentParticular: StudentParticular = {
    applicationId: id,
    applicationNumber,
    studentNumber,
    residenceType: processRawData(residenceType),
    offeredToYear,
    offeredToSemester,
    addressCountryRegion: getOptionsLabel(countryList, addressCountryRegion),
    jointPhDProgPartnerInstitution: (jointPhDProgPartnerInstitution as boolean)
      ? getOptionsLabel(rpgJoinPhdPartnerList, jointPhDProgPartnerInstitution)
      : undefined,
    collabPhDProgPartnerInstitution:
      (collabPhDProgPartnerInstitution as boolean)
        ? getOptionsLabel(
            prgCollabPhdPartnerList,
            collabPhDProgPartnerInstitution,
          )
        : undefined,
    nameInEnglishSurname,
    nameInEnglishGivenName,
    nameInChinese,
    identityType: processRawData(identityType),
    identityNumber,
    dateOfBirth: formatDateString(dateOfBirth, false) ?? 'N/A',
    gender,
    nationalityCountryRegion: getOptionsLabel(
      countryList,
      nationalityCountryRegion,
    ),
    nationalityProvince: getOptionsLabel(provinceOptions, nationalityProvince),
    addressLine1,
    addressLine2,
    addressLine3,
    contactPhoneAreaCode,
    contactPhoneNumber,
    mobileAreaCode,
    mobileNumber,
    emailAddress,
    localIndicator,
    disabilityType: getOptionsLabel(disabilityTypeList, disabilityType),
    disabilityDetail,
    photoFile: {
      id: photoFile?.id,
      link: photoFile?.link,
      name: photoFile?.name,
    },
    programmeCode,
    programmeSubcode,
    HKPFSInd,
  };

  return { studentParticular, loading, error, refetch };
};
